import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { safePost } from '../../../axios-default';
import Modal from '../../../components/Modal/modal';
import PercentageProgressRing from '../../../components/PercentageProgressRing';
import UploadImpressionTab from '../../../components/UploadImpressionTab/UploadImpressionTab';
import { UPSELL_TYPE } from '../../../store/TreatmentPlanApproval/TreatmentPlanApprovalSlice';
import { ClinicalApprovementOption } from '../clinicalApprovementOptionConstants';
import RestorationForm from './RestorationForm';
import { validationSchemaRestorationForm } from './RestorationFormQuestion';

export const UPSELL_RESTORATION_TOOL_FORM_TYPE = {
    RESTORATION_TOOL_FORM_WITH_NEW_SCAN: 'RESTORATION_TOOL_FORM_WITH_NEW_SCAN',
};

const RestorationToolFormModal = ({
    title = 'Restoration tool form',
    handleClose,
    open = false,
    isMarieGroup,
    caseId = null,
    onSuccess,
    onError,
    shouldSubmitRestorationFormWithNewScan = false,
}) => {
    const treatmentPlanApproval = useSelector((state) => state.treatmentPlanApproval);
    const uploadFile = useSelector((state) => state.uploadFile);
    const { planId, upsellType } = treatmentPlanApproval;
    const methods = useForm({
        resolver: yupResolver(validationSchemaRestorationForm),
        mode: 'onChange',
    });
    const progressBarRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    // Submit the upsell restoration form with the required new impression scan.
    const onSubmit = async (data) => {
        try {
            setIsLoading(true);
            let formData = new FormData();
            let caseImpressionData = undefined;

            if (shouldSubmitRestorationFormWithNewScan) {
                const impressionData = await uploadFile.handleImpressionData();
                formData = impressionData.impressionFile;
                if (impressionData && impressionData?.case_impression) {
                    caseImpressionData = { ...impressionData.case_impression, ...impressionData.newImpressionUpload };
                }
                if (caseImpressionData === undefined) {
                    throw new Error('Please provide impression data');
                }
            }

            let submitData = { ...data };
            submitData['isMarieGroup'] = isMarieGroup;
            submitData['is_upsell_case'] = true;

            const isApproveIDesignAndSmileDesign = [
                UPSELL_TYPE.PRE_ALIGNMENT_FIRST_Q14,
                UPSELL_TYPE.ALIGN_FIRST_Q16,
                UPSELL_TYPE.ALIGN_AND_RESTORE_Q14_AND_Q16,
            ].includes(upsellType);

            formData.append(
                'body',
                JSON.stringify({
                    restorationForm: submitData,
                    case_impression: caseImpressionData ? caseImpressionData : undefined,
                    upsellApprovementOption: isApproveIDesignAndSmileDesign
                        ? ClinicalApprovementOption['1']
                        : undefined,
                })
            );

            const result = await safePost(`case/restorationForm/${caseId}`, formData, {
                onUploadProgress: progressBarRef.current?.onProgressChange,
            });

            if (result && result?.success && onSuccess && typeof onSuccess === 'function') {
                onSuccess(planId);
                handleClose();
            }
        } catch (error) {
            console.error('Error in submitting restoration tool form: ', error);
            if (onError && typeof onError === 'function') {
                onError(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        methods.trigger();
    }, []);

    useEffect(() => {
        if (!open) {
            methods.reset();
        }
    }, [open]);

    return (
        <FormProvider {...methods}>
            <Modal
                className="prescriptionNotes"
                modal="prescriptionNotes"
                title={title}
                handleClose={handleClose}
                open={open}
                submitButton
                modalError={false}
                handleDataSave={methods.handleSubmit(onSubmit)}
            >
                <RestorationForm />
                {shouldSubmitRestorationFormWithNewScan && <UploadImpressionTab caseId={caseId} />}
            </Modal>
            <PercentageProgressRing ref={progressBarRef} open={isLoading} label={undefined} />
        </FormProvider>
    );
};

export default RestorationToolFormModal;
